import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { StarsButton } from '../components/StarsButton';
import { track } from '../lib/analytics';
import { api } from '../lib/api';
import { haptic } from '../lib/telegram';
import { cn, kickoffLabel } from '../lib/utils';

type Tab = '1X2' | 'BTTS' | 'SCORE';
type Pick1x2 = 'H' | 'D' | 'A';

export function PredictionSheet() {
  const { id } = useParams<{ id: string }>();
  const fixtureId = id ? Number.parseInt(id, 10) : NaN;
  const nav = useNavigate();
  const qc = useQueryClient();

  const fixture = useQuery({
    queryKey: ['fixture', fixtureId],
    queryFn: () => api.fixtureById(fixtureId),
    enabled: Number.isFinite(fixtureId),
  });

  // Single-fixture prediction lookup — avoids pulling the full history just to
  // hydrate the form.
  const existing = useQuery({
    queryKey: ['prediction', fixtureId],
    queryFn: () => api.fixtureMyPrediction(fixtureId),
    enabled: Number.isFinite(fixtureId),
  });
  const myPrediction = existing.data?.prediction ?? null;

  const [tab, setTab] = useState<Tab>('1X2');
  const [pick1x2, setPick1x2] = useState<Pick1x2 | null>(null);
  const [pickBtts, setPickBtts] = useState<boolean | null>(null);
  const [pickHome, setPickHome] = useState<number | null>(null);
  const [pickAway, setPickAway] = useState<number | null>(null);
  const [boost, setBoost] = useState(false);
  const [insurance, setInsurance] = useState(false);

  useEffect(() => {
    if (myPrediction) {
      setPick1x2((myPrediction.pick1x2 as Pick1x2 | null) ?? null);
      setPickBtts(myPrediction.pickBtts === null ? null : myPrediction.pickBtts === 1);
      setPickHome(myPrediction.pickHomeScore);
      setPickAway(myPrediction.pickAwayScore);
      setBoost(myPrediction.boostApplied === 1);
      setInsurance(myPrediction.insuranceApplied === 1);
    }
  }, [myPrediction]);

  const locked = fixture.data?.fixture.predictionWindowStatus === 'LOCKED';
  const hasAnyPick = pick1x2 !== null || pickBtts !== null || (pickHome !== null && pickAway !== null);
  // Purchase state comes from the server-side prediction row. The local toggles
  // (`boost`, `insurance`) represent intent-to-buy; these flags represent
  // already-paid so we can swap the UI to "Active ✓" and hide the Confirm button.
  const boostAlreadyActive = myPrediction?.boostApplied === 1;
  const insuranceAlreadyActive = myPrediction?.insuranceApplied === 1;

  const submit = useMutation({
    mutationFn: () =>
      api.predictionsSubmit({
        fixtureId,
        pick1x2,
        pickBtts,
        pickHomeScore: pickHome,
        pickAwayScore: pickAway,
        boostApplied: boost,
        insuranceApplied: insurance,
      }),
    onSuccess: async () => {
      haptic.success();
      let marketsPicked = 0;
      if (pick1x2 !== null) marketsPicked += 1;
      if (pickBtts !== null) marketsPicked += 1;
      if (pickHome !== null && pickAway !== null) marketsPicked += 1;
      track('prediction_submitted', {
        fixture_id: fixtureId,
        markets_picked: marketsPicked,
        boost,
        insurance,
        is_update: !!myPrediction,
      });
      await qc.invalidateQueries({ queryKey: ['prediction', fixtureId] });
      await qc.invalidateQueries({ queryKey: ['predictions', 'me'] });
      nav('/today');
    },
    onError: () => haptic.error(),
  });

  if (fixture.isLoading) {
    return (
      <div className="mx-auto max-w-md px-4 pt-10">
        <div className="h-32 animate-pulse rounded-xl bg-bg-card border border-border-base" />
      </div>
    );
  }
  if (!fixture.data) {
    return (
      <div className="mx-auto max-w-md px-4 pt-10 text-text-dim">Fixture not found.</div>
    );
  }

  const f = fixture.data.fixture;

  return (
    <div className="mx-auto max-w-md px-4 pt-4 pb-24 space-y-4">
      <button
        onClick={() => nav(-1)}
        className="font-display-alt text-xs uppercase tracking-widest text-text-dim"
      >
        ← Back
      </button>

      <Card variant="elevated" className="text-center space-y-1">
        <div className="font-display-alt text-xs uppercase tracking-widest text-text-faint">
          {kickoffLabel(f.kickoffAt)}
        </div>
        <div className="flex items-center justify-center gap-3 font-chunky text-xl">
          <div className="flex items-center gap-2">
            {f.homeTeamCrest && <img src={f.homeTeamCrest} alt="" className="w-7 h-7 object-contain" />}
            <span>{f.homeTeamName ?? `Team #${f.homeTeamId}`}</span>
          </div>
          <span className="text-text-faint font-mono text-sm">vs</span>
          <div className="flex items-center gap-2">
            <span>{f.awayTeamName ?? `Team #${f.awayTeamId}`}</span>
            {f.awayTeamCrest && <img src={f.awayTeamCrest} alt="" className="w-7 h-7 object-contain" />}
          </div>
        </div>
        {locked && (
          <div className="mt-2 inline-block rounded-md bg-bg-darker px-2 py-0.5 font-display-alt text-[10px] uppercase tracking-widest text-text-faint border border-border-base">
            Locked at kickoff
          </div>
        )}
      </Card>

      {/* Tabs */}
      <div className="grid grid-cols-3 rounded-lg border border-border-base bg-bg-card p-1">
        {(['1X2', 'BTTS', 'SCORE'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => {
              haptic.select();
              setTab(t);
            }}
            className={cn(
              'rounded-md py-2 font-display-alt text-xs uppercase tracking-widest transition-colors',
              tab === t ? 'bg-trophy-gold text-bg-night' : 'text-text-dim',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === '1X2' && (
        <div className="grid grid-cols-3 gap-2">
          {(['H', 'D', 'A'] as Pick1x2[]).map((p) => (
            <PickButton
              key={p}
              label={p === 'H' ? 'Home' : p === 'D' ? 'Draw' : 'Away'}
              selected={pick1x2 === p}
              disabled={locked}
              onClick={() => setPick1x2(p === pick1x2 ? null : p)}
            />
          ))}
        </div>
      )}

      {tab === 'BTTS' && (
        <div className="grid grid-cols-2 gap-2">
          <PickButton
            label="Yes"
            selected={pickBtts === true}
            disabled={locked}
            onClick={() => setPickBtts(pickBtts === true ? null : true)}
          />
          <PickButton
            label="No"
            selected={pickBtts === false}
            disabled={locked}
            onClick={() => setPickBtts(pickBtts === false ? null : false)}
          />
        </div>
      )}

      {tab === 'SCORE' && (
        <div className="grid grid-cols-5 gap-1.5">
          {Array.from({ length: 25 }, (_, i) => {
            const h = Math.floor(i / 5);
            const a = i % 5;
            const selected = pickHome === h && pickAway === a;
            return (
              <button
                key={i}
                onClick={() => {
                  haptic.select();
                  if (selected) {
                    setPickHome(null);
                    setPickAway(null);
                  } else {
                    setPickHome(h);
                    setPickAway(a);
                  }
                }}
                disabled={locked}
                className={cn(
                  'aspect-square rounded-md font-mono text-sm font-bold',
                  selected
                    ? 'bg-trophy-gold text-bg-night'
                    : 'bg-bg-card border border-border-base text-text-base',
                )}
              >
                {h}-{a}
              </button>
            );
          })}
        </div>
      )}

      {/* Boost — already-active vs not-yet-purchased */}
      {boostAlreadyActive ? (
        <Card className="border-trophy-gold/40">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-chunky text-sm text-trophy-gold">Boost active ✓</div>
              <div className="text-xs text-text-dim mt-0.5">
                2× points if any market scores
              </div>
            </div>
            <span className="font-mono text-xs text-text-faint">75 ⭐ paid</span>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-chunky text-sm">Boost (2× points)</div>
              <div className="text-xs text-text-dim mt-0.5">75 ⭐ per fixture</div>
            </div>
            <button
              onClick={() => {
                haptic.tap();
                setBoost((b) => !b);
              }}
              disabled={locked}
              className={cn(
                'h-7 w-12 rounded-full transition-colors',
                boost ? 'bg-trophy-gold' : 'bg-bg-elevated',
              )}
            >
              <span
                className={cn(
                  'block h-6 w-6 rounded-full bg-white shadow transition-transform',
                  boost ? 'translate-x-5' : 'translate-x-0.5',
                )}
              />
            </button>
          </div>
          {boost && (
            <div className="mt-3">
              {myPrediction ? (
                <StarsButton
                  productCode="BOOST"
                  fixtureId={f.id}
                  label="Confirm boost"
                  stars={75}
                  variant="primary"
                  fullWidth
                  invalidateKeys={[['prediction', f.id], ['me']]}
                  onSuccess={() => track('boost_purchased', { fixture_id: f.id })}
                />
              ) : (
                <div className="text-xs text-text-faint text-center">
                  Lock in your prediction below first, then come back to confirm Boost.
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Insurance — already-active vs not-yet-purchased */}
      {insuranceAlreadyActive ? (
        <Card className="border-premium/40">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-chunky text-sm text-premium">Insurance active ✓</div>
              <div className="text-xs text-text-dim mt-0.5">
                7-pt refund if all markets miss
              </div>
            </div>
            <span className="font-mono text-xs text-text-faint">90 ⭐ paid</span>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-chunky text-sm">Insurance (7-pt refund)</div>
              <div className="text-xs text-text-dim mt-0.5">
                90 ⭐ — get 7 pts back if all markets miss
              </div>
            </div>
            <button
              onClick={() => {
                haptic.tap();
                setInsurance((v) => !v);
              }}
              disabled={locked}
              className={cn(
                'h-7 w-12 rounded-full transition-colors',
                insurance ? 'bg-premium' : 'bg-bg-elevated',
              )}
            >
              <span
                className={cn(
                  'block h-6 w-6 rounded-full bg-white shadow transition-transform',
                  insurance ? 'translate-x-5' : 'translate-x-0.5',
                )}
              />
            </button>
          </div>
          {insurance && (
            <div className="mt-3">
              {myPrediction ? (
                <StarsButton
                  productCode="INSURANCE"
                  fixtureId={f.id}
                  label="Confirm insurance"
                  stars={90}
                  variant="primary"
                  fullWidth
                  invalidateKeys={[['prediction', f.id], ['me']]}
                  onSuccess={() => track('insurance_purchased', { fixture_id: f.id })}
                />
              ) : (
                <div className="text-xs text-text-faint text-center">
                  Lock in your prediction below first, then come back to confirm Insurance.
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {!locked && (
        <Button
          variant="gold"
          fullWidth
          disabled={!hasAnyPick || submit.isPending}
          onClick={() => submit.mutate()}
        >
          {submit.isPending ? 'Submitting…' : myPrediction ? 'Update prediction' : 'Lock it in'}
        </Button>
      )}
    </div>
  );
}

function PickButton({
  label,
  selected,
  disabled,
  onClick,
}: {
  label: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={() => {
        haptic.tap();
        onClick();
      }}
      disabled={disabled}
      className={cn(
        'rounded-xl py-5 font-chunky text-base uppercase border-2 transition-colors min-h-[60px]',
        selected
          ? 'bg-trophy-gold border-trophy-gold text-bg-night'
          : 'bg-bg-card border-border-base text-text-base',
        disabled && 'opacity-50',
      )}
    >
      {label}
    </button>
  );
}
